import { useState, useEffect } from 'react'
import { InputForm } from './components/InputForm'
import { MarketingReportDisplay } from './components/MarketingReportDisplay'
import { HistorySidebar } from './components/HistorySidebar'
import { AdminDashboard } from './components/AdminDashboard'
import { ContactForm } from './components/ContactForm'
import type { MarketingReport } from './types'
import { historyService } from './services/historyService'
import { analyticsService } from './services/analyticsService'
import type { ChatSession } from './services/aiService'

function App() {
  const [currentReport, setCurrentReport] = useState<MarketingReport | null>(null)
  const [chatSession, setChatSession] = useState<ChatSession | null>(null)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showAdmin, setShowAdmin] = useState(false)
  const [showContact, setShowContact] = useState(false)

  useEffect(() => {
    // Track page view on mount
    analyticsService.trackPageView()

    // Check if we're on the admin route
    if (window.location.pathname === '/admin/stats') {
      setShowAdmin(true)
    }
  }, [])

  const handleReportGenerated = (report: MarketingReport, session: ChatSession, fromCache: boolean = false) => {
    setCurrentReport(report)
    setChatSession(session)
    historyService.saveReport(report)
    analyticsService.trackReportGenerated({
      model: report.modelUsed,
      regions: report.selectedRegions,
      mediaType: report.mediaType,
      campaignType: report.campaignType,
      fromCache,
    })
  }

  const handleLoadReport = (report: MarketingReport) => {
    setCurrentReport(report)
    setChatSession(null) // Chat session can't be restored from history
    setIsHistoryOpen(false)
  }

  const handleNewReport = () => {
    setCurrentReport(null)
    setChatSession(null)
  }

  if (showAdmin) {
    return <AdminDashboard onBack={() => setShowAdmin(false)} />
  }

  return (
    <div className="min-h-screen grid-bg">
      {/* Header */}
      <header className="border-b border-cyber-blue/20 bg-cyber-dark/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <h1
                className="cyber-heading text-xl sm:text-2xl cursor-pointer"
                onClick={handleNewReport}
              >
                Entertainment Marketing AI
              </h1>
              <span className="text-xs font-mono text-cyber-purple border border-cyber-purple/50 px-2 py-0.5 rounded">
                v3.0
              </span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowContact(true)}
                className="text-sm text-gray-400 hover:text-cyber-pink transition-colors"
              >
                Contact
              </button>
              <button
                onClick={() => setShowAdmin(true)}
                className="text-sm text-gray-400 hover:text-cyber-blue transition-colors"
              >
                Admin
              </button>
              <button
                onClick={() => setIsHistoryOpen(true)}
                className="cyber-button-secondary flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!currentReport ? (
          <InputForm
            onReportGenerated={handleReportGenerated}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            onShowContact={() => setShowContact(true)}
          />
        ) : (
          <MarketingReportDisplay
            key={currentReport.id}
            report={currentReport}
            chatSession={chatSession}
            onNewReport={handleNewReport}
          />
        )}
      </main>

      {/* History Sidebar */}
      <HistorySidebar
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        onLoadReport={handleLoadReport}
      />

      {/* Contact Form */}
      <ContactForm
        isOpen={showContact}
        onClose={() => setShowContact(false)}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-cyber-darker/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="cyber-card text-center max-w-md">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto border-4 border-cyber-blue border-t-transparent rounded-full animate-spin" />
            </div>
            <h3 className="cyber-heading text-lg mb-2">Generating Strategy</h3>
            <p className="text-gray-400 text-sm">
              Our AI strategists are analyzing data and crafting your personalized marketing plan...
            </p>
            <div className="mt-4 flex justify-center gap-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-cyber-pink rounded-full loading-pulse"
                  style={{ animationDelay: `${i * 0.2}s` }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-cyber-blue/20 bg-cyber-dark/50 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-xs text-gray-500">
            Built with Gemini by Hardie Tankersley
          </p>
        </div>
      </footer>
    </div>
  )
}

export default App
